import React, { useEffect, useState } from "react";
import { CompanyIncomeStatement } from "../../company";
import { useOutletContext } from "react-router-dom";
import { getIncomeStatement } from "../../api";
import Table from "../Table/Table";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {
  formatLargeMonetaryNumber,
  formatLargeNonMonetaryNumber,
  formatRatio,
} from "../Helpers/NumberFormatting";

interface Props {}

const configs = [
  {
    label: "Date",
    render: (company: CompanyIncomeStatement) => company.date,
  },
  {
    label: "Fiscal Year",
    render: (company: CompanyIncomeStatement) => company.fiscalYear,
  },
  {
    label: "Revenue (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.revenue || 0)),
  },
  {
    label: "Cost of Revenue (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.costOfRevenue || 0)),
  },
  {
    label: "Gross Profit (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.grossProfit || 0)),
  },
  {
    label: "Operating Expenses (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.operatingExpenses || 0)),
  },
  {
    label: "R&D Expenses (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.researchAndDevelopmentExpenses || 0)),
  },
  {
    label: "SG&A Expenses (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.sellingGeneralAndAdministrativeExpenses || 0)),
  },
  {
    label: "Depreciation & Amortization (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.depreciationAndAmortization || 0)),
  },
  {
    label: "EBITDA (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.ebitda || 0)),
  },
  {
    label: "Operating Income (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.operatingIncome || 0)),
  },
  {
    label: "Income Before Tax (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.incomeBeforeTax || 0)),
  },
  {
    label: "Income Tax Expense (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.incomeTaxExpense || 0)),
  },
  {
    label: "Net Income (USD)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeMonetaryNumber(Number(company.netIncome || 0)),
  },
  {
    label: "EPS (Basic)",
    render: (company: CompanyIncomeStatement) => formatRatio(Number(company.eps || 0)),
  },
  {
    label: "EPS (Diluted)",
    render: (company: CompanyIncomeStatement) => formatRatio(Number(company.epsDiluted || 0)),
  },
  {
    label: "Weighted Shares Outstanding",
    render: (company: CompanyIncomeStatement) =>
      formatLargeNonMonetaryNumber(Number(company.weightedAverageShsOut || 0)),
  },
  {
    label: "Weighted Shares (Diluted)",
    render: (company: CompanyIncomeStatement) =>
      formatLargeNonMonetaryNumber(Number(company.weightedAverageShsOutDil || 0)),
  },
];

const IncomeStatement = (props: Props) => {
  const ticker = useOutletContext<string>();
  const [incomeStatement, setIncomeStatement] = useState<CompanyIncomeStatement[]>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const incomeStatementFetch = async () => {
      try {
        const result = await getIncomeStatement(ticker);
        setIncomeStatement(result!.data);
      } catch (err: any) {
        setError(err.message || "Something went wrong while fetching data.");
      } finally {
        setLoading(false);
      }
    };
    incomeStatementFetch();
  }, []);

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 font-medium">⚠️ Error fetching data: {error}</div>;
  }

  return (
    <>
      {incomeStatement ? (
        <>
          <Table config={configs} data={incomeStatement} />
        </>
      ) : (
        <>
          <div className="text-gray-500">No data available.</div>
        </>
      )}
    </>
  );
};

export default IncomeStatement;
